import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import showcaseApi from "../../lib/showcaseApi";

export const RoadmapVisibilityContext = createContext(null);
export const useRoadmapVisibilityContext = () => useContext(RoadmapVisibilityContext);

export const markRoadmapItem = (data, key, excluded, id) => {
  if (!data) return data;
  const mark = (item) => item.key === key ? { ...item, excluded_from_presentation: excluded, hidden_record_id: id } : item;
  const owner = (value) => {
    if (!value) return value;
    const copy = { ...value };
    for (const field of ["done", "next", "blocked", "attention"]) if (copy[field]) copy[field] = copy[field].map(mark);
    return copy;
  };
  const copy = { ...data, hub: owner(data.hub), products: data.products.map(owner) };
  for (const field of ["blockers", "wins", "attention"]) if (copy[field]) copy[field] = copy[field].map(mark);
  return copy;
};

export const presentationRoadmap = (data) => {
  if (!data) return data;
  const visible = (items = []) => items.filter((item) => !item.excluded_from_presentation);
  const owner = (value) => {
    if (!value) return value;
    const copy = { ...value, empty_messages: { ...value.empty_messages } };
    for (const field of ["done", "next", "blocked", "attention"]) {
      if (!value[field]) continue;
      copy[field] = visible(value[field]);
      if (value[field].length && !copy[field].length) copy.empty_messages[field] = "No selected updates";
    }
    return copy;
  };
  const copy = { ...data, products: data.products.map(owner), hub: owner(data.hub) };
  for (const field of ["blockers", "wins", "attention"]) if (copy[field]) copy[field] = visible(copy[field]);
  return copy;
};

export const useRoadmapVisibility = (onChanged) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try { const result = await showcaseApi.hiddenItems(); setItems(result.items || []); }
    catch (err) { setError(err.message || "Could not load hidden items."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const restore = async (id, itemKey) => {
    const key = itemKey || items.find((item) => item.id === id)?.item_key;
    setPending((current) => ({ ...current, [key]: true }));
    setErrors((current) => ({ ...current, [key]: "" }));
    try {
      const result = await showcaseApi.restoreItem(id);
      onChanged(result.item_key, false, null);
      setItems((current) => current.filter((item) => item.id !== id));
      toast.success("Item restored to Presentation Mode");
      return true;
    } catch (err) {
      if (err.status === 404 && key) {
        onChanged(key, false, null);
        await refresh();
        return true;
      }
      setErrors((current) => ({ ...current, [key]: err.message }));
      toast.error(err.message || "Could not restore that item.");
      return false;
    } finally { setPending((current) => ({ ...current, [key]: false })); }
  };
  const exclude = async (item, product) => {
    setPending((current) => ({ ...current, [item.key]: true }));
    setErrors((current) => ({ ...current, [item.key]: "" }));
    try {
      const result = await showcaseApi.hideItem(item.key, product?.slug);
      onChanged(item.key, true, result.item.id);
      setItems((current) => [result.item, ...current.filter((entry) => entry.id !== result.item.id)]);
      toast.success("Greyed out here; hidden in Presentation Mode", { action: { label: "Undo", onClick: () => restore(result.item.id, item.key) } });
    } catch (err) {
      setErrors((current) => ({ ...current, [item.key]: err.message }));
      toast.error(err.message || "Could not hide that item.");
    } finally { setPending((current) => ({ ...current, [item.key]: false })); }
  };
  return { items, loading, error, pending, errors, refresh, exclude, restore };
};
